import {
  REENTRY_PLANNING_TAB_CONFIG,
  REENTRY_PLANNING_TABS,
  REENTRY_PLANS_LIST_SCOPES,
} from '../constants/reentry-planning.constants';

const [DEFAULT_REENTRY_PLANNING_TAB] = REENTRY_PLANNING_TAB_CONFIG;

/** Tab metadata (label, list scope, aria ids) for the active tab; falls back to the default tab. */
export const resolveReentryPlanningTab = (activeTabLabel) =>
  REENTRY_PLANNING_TAB_CONFIG.find((tab) => tab.label === activeTabLabel) ??
  DEFAULT_REENTRY_PLANNING_TAB;

export const isMyPlansScope = (listScope) => listScope === REENTRY_PLANS_LIST_SCOPES.MY;

const TAB_VISIBILITY_PREDICATES = {
  [REENTRY_PLANNING_TABS.MY_PLANS]: ({ canViewMyPlans }) => Boolean(canViewMyPlans),
  [REENTRY_PLANNING_TABS.ALL_PLANS]: ({ canViewAllPlansList }) => Boolean(canViewAllPlansList),
};

const isReentryPlanningTabVisible = (visibility, tab) => {
  const isVisible = TAB_VISIBILITY_PREDICATES[tab.label];
  return isVisible ? isVisible(visibility) : false;
};

/**
 * Filters FolderNav tabs so My Plans only appears for assigned-detail holders.
 * All Plans stays available for module access, including preview-release staff.
 */
export const resolveVisibleReentryPlanningTabs = ({
  canViewAllPlansList = false,
  canViewMyPlans = false,
  folderTabs = [],
} = {}) =>
  folderTabs.filter((tab) => isReentryPlanningTabVisible({ canViewAllPlansList, canViewMyPlans }, tab));

/** Keeps the active tab on a visible one; falls back to the first visible tab. */
export const resolveActiveReentryPlanningTabLabel = (activeTabLabel, visibleTabs = []) => {
  const matchingTab = visibleTabs.find((tab) => tab.value === activeTabLabel);
  return matchingTab?.value ?? visibleTabs[0]?.value ?? DEFAULT_REENTRY_PLANNING_TAB.label;
};

/**
 * Shared View Plan rule.
 * My Plans requires the assigned-detail (list) permission.
 * All Plans requires preview LFA + preview-release, or reentry LFA + view-any-detail.
 * Editor membership alone is not enough, because assigned-detail holders without reentry LFA
 * open those plans from My Plans.
 * Hidden when the permission is missing — never shown disabled.
 */
const resolveViewPlanAvailability = ({ canViewAssignedPlans, canViewAllPlans, listScope }) =>
  isMyPlansScope(listScope) ? Boolean(canViewAssignedPlans) : Boolean(canViewAllPlans);

/**
 * Whether a single row shows View Plan.
 *
 * @param {boolean} canViewAssignedPlans - Holder of the assigned-detail / list permission.
 * @param {boolean} canViewAllPlans - Holder of case_plan_reentry_view_any_detail.
 * @param {string} listScope - Active tab scope (`my` or `all`).
 * @returns {boolean} True when the row's View Plan action should render.
 */
export const resolveCanViewPlanRow = ({
  canViewAssignedPlans = false,
  canViewAllPlans = false,
  listScope,
} = {}) => resolveViewPlanAvailability({ canViewAssignedPlans, canViewAllPlans, listScope });

/**
 * Whether the action column renders at all.
 *
 * @param {boolean} canViewAssignedPlans - Holder of the assigned-detail / list permission.
 * @param {boolean} canViewAllPlans - Holder of case_plan_reentry_view_any_detail.
 * @param {string} listScope - Active tab scope (`my` or `all`).
 * @returns {boolean} True when the action column should render.
 */
export const resolveCanViewPlanColumn = ({
  canViewAssignedPlans = false,
  canViewAllPlans = false,
  listScope,
} = {}) => resolveViewPlanAvailability({ canViewAssignedPlans, canViewAllPlans, listScope });
